import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DownloadIcon, FileTextIcon, PenLineIcon, SendIcon, Trash2Icon } from 'lucide-react'
import {
	Badge,
	Button,
	ConfirmDialog,
	DefinitionList,
	DefinitionRow,
	Separator,
} from '@rentflowdev/ui'
import {
	useDeleteDocumentMutation,
	useListDocumentSignaturesQuery,
	useRenderDocumentMutation,
	useUpdateDocumentStatusMutation,
} from '@/api'
import { SignDocumentDialog } from '@/components/sign-document-dialog'
import {
	DEFAULT_LOCALE,
	DOCUMENT_STATUS_KEYS,
	DOCUMENT_STATUS_VARIANTS,
	DOCUMENT_TYPE_KEYS,
} from '@/types'
import type { RegistryDocument } from '@/types'

function formatDate(iso: string) {
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, {
		day: '2-digit', month: '2-digit', year: 'numeric',
	})
}

interface DocumentSidebarProps {
	doc:        RegistryDocument
	onDeleted?: () => void
	onSigned?:  () => void
}

export function DocumentSidebar({ doc, onDeleted, onSigned }: DocumentSidebarProps) {
	const { t } = useTranslation()
	const [signOpen,   setSignOpen]   = useState(false)
	const [deleteOpen, setDeleteOpen] = useState(false)

	const [updateStatus, { isLoading: isUpdating }] = useUpdateDocumentStatusMutation()
	const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation()
	const [renderDocument, { isLoading: isRendering }] = useRenderDocumentMutation()
	const { data: signaturesData } = useListDocumentSignaturesQuery(doc.id)
	const signatures = signaturesData?.signatures ?? []

	const canSign = doc.status === 'DRAFT' || doc.status === 'DATA_FILLED'
	const canSend = doc.status === 'SIGNED_BY_ME'

	return (
		<div className="flex h-full flex-col overflow-y-auto">
			<div className="flex flex-col gap-2 border-b border-(--color-border) p-4">
				<p className="text-sm font-[600] leading-5 text-(--color-foreground) line-clamp-3">
					{doc.title}
				</p>
				<Badge
					variant={DOCUMENT_STATUS_VARIANTS[doc.status]}
					size="sm"
					className="self-start"
				>
					{t(DOCUMENT_STATUS_KEYS[doc.status])}
				</Badge>
			</div>

			<div className="flex flex-1 flex-col gap-4 p-4">
				<DefinitionList>
					<DefinitionRow
						label={t('documents.table.document')}
						value={t(DOCUMENT_TYPE_KEYS[doc.type] ?? doc.type)}
					/>
					{doc.propertyTitle && (
						<DefinitionRow
							label={t('documents.table.property')}
							value={doc.propertyTitle}
						/>
					)}
					{doc.rentalTitle && (
						<DefinitionRow
							label={t('documents.table.rental')}
							value={doc.rentalTitle}
						/>
					)}
					<DefinitionRow
						label={t('documents.table.createdAt')}
						value={formatDate(doc.createdAt)}
					/>
				</DefinitionList>

				{signatures.length > 0 && (
					<div className="flex flex-col gap-2">
						<p className="text-xs font-[500] uppercase tracking-wide text-(--color-muted-foreground)">
							{t('documents.signatures.title')}
						</p>
						{signatures.map((sig) => (
							<div key={sig.id} className="flex flex-col gap-0.5 rounded-(--radius-md) border border-(--color-border) px-3 py-2">
								<p className="text-sm font-[500] text-(--color-foreground)">
									{t(`documents.signatures.methods.${sig.signingMethod}`, { defaultValue: sig.signingMethod })}
								</p>
								<p className="text-xs text-(--color-muted-foreground)">
									{new Date(sig.signedAt).toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
								</p>
							</div>
						))}
					</div>
				)}

				<div className="flex flex-col gap-2">
					{doc.fileUrl ? (
						<Button
							variant="outline"
							className="w-full"
							iconLeft={<DownloadIcon className="size-4" />}
							onClick={() => window.open(doc.fileUrl, '_blank')}
						>
							{t('documents.actions.download')}
						</Button>
					) : (
						<Button
							variant="outline"
							className="w-full"
							iconLeft={<FileTextIcon className="size-4" />}
							disabled={isRendering}
							onClick={() => void renderDocument(doc.id)}
						>
							{t('documents.actions.generatePdf', { defaultValue: 'Сгенерировать PDF' })}
						</Button>
					)}
					<Button
						variant="outline"
						className="w-full"
						iconLeft={<PenLineIcon className="size-4" />}
						disabled={!canSign}
						onClick={() => setSignOpen(true)}
					>
						{t('documents.actions.sign')}
					</Button>
					<SignDocumentDialog
						open={signOpen}
						onOpenChange={setSignOpen}
						documentId={doc.id}
						onSigned={onSigned}
					/>
					<Button
						variant="outline"
						className="w-full"
						iconLeft={<SendIcon className="size-4" />}
						disabled={!canSend || isUpdating}
						loading={isUpdating}
						onClick={() => void updateStatus({ id: doc.id, body: { status: 'SENT' } })}
					>
						{t('documents.actions.send')}
					</Button>
				</div>

				<div className="mt-auto flex flex-col gap-2 pt-2">
					<Separator />
					<Button
						variant="ghost"
						className="w-full text-(--color-destructive) hover:text-(--color-destructive)"
						iconLeft={<Trash2Icon className="size-4" />}
						loading={isDeleting}
						onClick={() => setDeleteOpen(true)}
					>
						{t('documents.actions.delete')}
					</Button>
				</div>
			</div>

			<ConfirmDialog
				open={deleteOpen}
				onOpenChange={setDeleteOpen}
				title={t('documents.actions.deleteConfirm', { title: doc.title })}
				confirmLabel={t('common.delete')}
				cancelLabel={t('common.cancel')}
				variant="destructive"
				loading={isDeleting}
				onConfirm={async () => {
					await deleteDocument(doc.id).unwrap()
					onDeleted?.()
				}}
			/>
		</div>
	)
}
